import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import queryString from "query-string";
import { LabelT, MailDataI, SubUserT } from "../../models/models";

const searchParams = queryString.parse(window.location.search);

const FIRST_PAGE_NUM = 1;
export const DEFAULT_ITEMS_PER_PAGE = 10;

export type MailStatusT = "active" | "uploading" | "pendingRemoved" | "removed";

export type SubUserSentMailsT = "my" | "organization";

export interface MailsCountI {
  outbox?: number;
  inbox?: number;
  all?: number;
  trash?: number;
  jmail?: number;
  jform?: number;
  jpay?: number;
  labels?: Record<number, number>;
  received?: number;
  sent?: number;
}

export interface SearchParamsI {
  q?: string; // Search query
  l?: string[]; // Label.
  sm?: MailStatusT[]; // Mail status filters
  sf?: string[];
  sa?: string[]; // Mail action filters
  su?: string[]; // Sub user emails
  rcp?: SubUserSentMailsT[];
}

interface InitialStateI {
  // Status of fetching ReactGetSentMails
  isFetching: boolean;
  // Comes from the ReactGetSentMails
  mailsQuantity: number;
  // List of selected mails with checkboxes
  selectedMailIds: string[];
  // Selected mail for preview on the split view
  selectedMailId: string | null;
  hoveredMailId: string | null;
  // Value of the checkboxes presets dropdown
  currentSelectFilter: string;
  // Search params are partially stored in the local storage
  searchParams: {
    t: "all" | "sent" | "received" | "picker" | "trash"; // Mail type
    f: "jmail" | "jform" | "jpay" | "picker" | ""; // Mail category
    all: SearchParamsI;
    sent: SearchParamsI;
    received: SearchParamsI;
    picker: {
      q: string; // Search query
      ps: string[]; // Picker statuses
      p: "active" | "archived";
    };
    trash: SearchParamsI;
    sortBy: "date" | "size" | "number" | "downloadsQuantity";
    isSortAsc: boolean;
    itemsPerPage: 10 | 20 | 50 | 100;
    pn: number; // Page number
  };
  mailsData: {
    [encryptedMailId: string]: MailDataI;
  };
  splitView: boolean;
  sideInboxFiles: any | null;
  isFetchingSideInboxFiles: boolean;
  labels: Record<string, LabelT>;
  mailsCount?: MailsCountI;
  formList: Record<string, any>;
  abortController: AbortController | null;
  subUserList: {
    [subUserId: string]: SubUserT;
  };
  apiEnable?: boolean;
  ftpEnable?: boolean;
  previewMail?: string;
}

const searchParamsInitialState: SearchParamsI = {
  q: "",
  l: [],
  sm: ["active"],
  sf: [],
  sa: [],
  su: [],
  rcp: [],
};

export const inboxInitialState: InitialStateI = {
  isFetching: true,
  isFetchingSideInboxFiles: false,
  mailsData: {},
  sideInboxFiles: null,
  mailsQuantity: 0,
  selectedMailIds: [],
  selectedMailId: null,
  hoveredMailId: null,
  currentSelectFilter: "",
  searchParams: {
    t: "all",
    f: "",
    all: searchParamsInitialState,
    sent: searchParamsInitialState,
    received: searchParamsInitialState,
    picker: {
      q: "",
      ps: [],
      p: "active",
    },
    trash: {
      ...searchParamsInitialState,
      sm: [],
    },
    sortBy: "date",
    isSortAsc: true,
    itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
    pn: FIRST_PAGE_NUM,
  },
  splitView: searchParams.t !== "picker",
  labels: {},
  formList: {},
  abortController: null,
  subUserList: {},
};

const inboxSlice = createSlice({
  name: "inbox",
  initialState: inboxInitialState,
  reducers: {
    setSorting: (
      state,
      action: PayloadAction<{
        sortBy: InitialStateI["searchParams"]["sortBy"];
        isSortAsc: InitialStateI["searchParams"]["isSortAsc"];
      }>
    ) => {
      state.searchParams.sortBy = action.payload.sortBy;
      state.searchParams.isSortAsc = action.payload.isSortAsc;
    },
    /**
     * @deprecated This action is deprecated and will be removed in future versions.
     * Use setHoveredMailId instead.
     */
    setHoveredMail: (state, action: PayloadAction<MailDataI | null>) => {
      state.hoveredMailId = action.payload ? action.payload.id : null;
    },
    setHoveredMailId: (state, action: PayloadAction<string | null>) => {
      state.hoveredMailId = action.payload;
    },
    /**
     * @deprecated This action is deprecated and will be removed in future versions.
     * Use setSelectedMailId instead.
     */
    setSelectedMail: (state, action: PayloadAction<MailDataI | null>) => {
      state.selectedMailId = action.payload ? action.payload.id : null;
    },
    setSelectedMailId: (state, action: PayloadAction<string | null>) => {
      state.selectedMailId = action.payload;
    },
    setSearchParams: (state, action: PayloadAction<SearchParamsI>) => {
      for (const param in action.payload) {
        const tParam = state.searchParams.t;

        if (tParam !== "picker") {
          (state.searchParams as any)[tParam][param as keyof SearchParamsI] =
            action.payload[param as keyof SearchParamsI];
        }
      }
    },
    setPickerSearchParams: (
      state,
      action: PayloadAction<Partial<InitialStateI["searchParams"]["picker"]>>
    ) => {
      for (const param in action.payload) {
        (state.searchParams.picker as any)[param] =
          action.payload[
            param as keyof InitialStateI["searchParams"]["picker"]
          ];
      }
    },
    setPageNumber: (state, action: PayloadAction<number>) => {
      state.searchParams.pn = action.payload;
    },
    setPageMailCategory: (
      state,
      action: PayloadAction<InitialStateI["searchParams"]["f"]>
    ) => {
      state.searchParams.f = action.payload;
    },
    setPageMailType: (
      state,
      action: PayloadAction<InitialStateI["searchParams"]["t"]>
    ) => {
      state.searchParams.t = action.payload;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.searchParams.pn = action.payload;
    },
    setIsFetching: (state, action: PayloadAction<boolean>) => {
      state.isFetching = action.payload;
    },
    setAbortController: (state, action: PayloadAction<AbortController>) => {
      state.abortController = action.payload;
    },
    setMailsData: (state, action: PayloadAction<Record<string, MailDataI>>) => {
      state.mailsData = action.payload;
    },
    setMailData: (
      state,
      action: PayloadAction<{
        mailId: string;
        mailData: MailDataI;
      }>
    ) => {
      state.mailsData[action.payload.mailId] = action.payload.mailData;
    },
    setMailDataSecurity: (
      state,
      action: PayloadAction<{
        mailId: string;
        security: MailDataI["security"];
      }>
    ) => {
      state.mailsData[action.payload.mailId].security = action.payload.security;
    },
    setMailsQuantity: (state, action: PayloadAction<number>) => {
      state.mailsQuantity = action.payload;
    },
    setSelectedMailIds: (state, action: PayloadAction<any[]>) => {
      state.selectedMailIds = action.payload;
    },
    setApiEnable: (state, action: PayloadAction<boolean>) => {
      state.apiEnable = action.payload;
    },
    setFtpEnable: (state, action: PayloadAction<boolean>) => {
      state.ftpEnable = action.payload;
    },
    setMailsCount: (state, action: PayloadAction<MailsCountI>) => {
      state.mailsCount = action.payload;
    },
    removeMailFromState: (state, action: PayloadAction<string>) => {
      delete state.mailsData[action.payload];
    },
    setCurrentSelectFilter: (state, action: PayloadAction<string>) => {
      state.currentSelectFilter = action.payload;
    },
    setSplitView: (state, action: PayloadAction<boolean>) => {
      state.splitView = action.payload;
    },
    setPreviewMail: (
      state,
      action: PayloadAction<InitialStateI["previewMail"]>
    ) => {
      state.previewMail = action.payload;
    },
    setIsFetchingSideInboxFiles: (state, action: PayloadAction<boolean>) => {
      state.isFetchingSideInboxFiles = action.payload;
    },
    setSideInboxFiles: (state, action: PayloadAction<any>) => {
      state.sideInboxFiles = action.payload;
    },
    setFormList: (state, action: PayloadAction<Record<string, any>>) => {
      state.formList = action.payload;
    },
    setLabels: (state, action: PayloadAction<Record<number, LabelT>>) => {
      state.labels = action.payload;
    },
    setLabel: (state, action: PayloadAction<LabelT>) => {
      state.labels[action.payload.id] = action.payload;
    },
    removeLabel: (state, action: PayloadAction<number>) => {
      delete state.labels[action.payload];
    },
    setSubUserList: (
      state,
      action: PayloadAction<{
        [subUserId: string]: SubUserT;
      }>
    ) => {
      state.subUserList = action.payload;
    },
    setItemsPerPage: (
      state,
      action: PayloadAction<InitialStateI["searchParams"]["itemsPerPage"]>
    ) => {
      state.searchParams.itemsPerPage = action.payload;
    },
    setMailIsSaved: (
      state,
      action: PayloadAction<{ mailId: string; isSaved: boolean }>
    ) => {
      state.mailsData[action.payload.mailId].isSaved = action.payload.isSaved;
    },
    setMailStatus: (
      state,
      action: PayloadAction<{ mailId: string; status: MailDataI["mailStatus"] }>
    ) => {
      state.mailsData[action.payload.mailId].mailStatus = action.payload.status;
    },
  },
});

export default inboxSlice.reducer;

export const {
  setSorting,
  /**
   * @deprecated This action is deprecated and will be removed in future versions.
   */
  setHoveredMail,
  setHoveredMailId,
  /**
   * @deprecated This action is deprecated and will be removed in future versions.
   */
  setSelectedMail,
  setPickerSearchParams,
  setSearchParams,
  setCurrentPage,
  setIsFetching,
  setAbortController,
  setMailsData,
  setMailData,
  setMailDataSecurity,
  setMailsQuantity,
  setSelectedMailId,
  setSelectedMailIds,
  setApiEnable,
  setFtpEnable,
  setMailsCount,
  removeMailFromState,
  setCurrentSelectFilter,
  setSplitView,
  setPreviewMail,
  setIsFetchingSideInboxFiles,
  setSideInboxFiles,
  setFormList,
  setLabels,
  setLabel,
  removeLabel,
  setSubUserList,
  setPageMailType,
  setPageMailCategory,
  setPageNumber,
  setItemsPerPage,
  setMailIsSaved,
  setMailStatus,
} = inboxSlice.actions;
